import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TransitionGroup } from 'react-transition-group';
import { Fade, makeStyles } from '@material-ui/core';
import ActionPlanCard from './actionPlanCard';
import PreviewDescriptionDialog from '../previewDescriptionDialog';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { hasScopedAuthPermissionCheck } from '../../../utility/permissions';

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    boxSizing: 'border-box',
    width: '100%',
  },
  actionPlanWrapper: {
    marginBottom: spacing(4),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
}));

const ActionPlanCards = ({
  className = undefined,
  cardClass = undefined,
  translations,
  auth,
  accessControl,
  actionPlans = [],
  canViewCourses,
  statuses,
  user = null,
  isDisabled = false,
  canAccessUserProfileCheck,
  onCardClick = () => {},
  onDelete = () => {},
  onEdit = () => {},
  onManageTasks = () => {},
  onDismissProposal = () => {},
  goToUserProfilePage,
}) => {
  const classes = useStyles();

  const [selectedActionPlan, setSelectedActionPlan] = useState(null);

  const canView = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.PLANS_VIEW,
    accessControl,
    auth.id,
    user?.id
  );

  const canEdit = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.PLANS_EDIT,
    accessControl,
    auth.id,
    user?.id
  );
  const canDelete = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.PLANS_DELETE,
    accessControl,
    auth.id,
    user?.id
  );
  const isCreatedForCurrentUser = auth.id === user?.id;
  const canAcceptProposal = !isCreatedForCurrentUser && canEdit;

  const handleCloseDescriptionDialog = () => setSelectedActionPlan(null);

  const handleOpenDescriptionDialog = actionPlan =>
    setSelectedActionPlan(actionPlan);

  return (
    <div className={classNames(classes.root, className)}>
      <TransitionGroup>
        {actionPlans.map(aPlan => {
          const isCreatedByMe = auth.id === aPlan?.creator?.id;
          const isCreatorAccessible =
            !!aPlan?.creator && canAccessUserProfileCheck(aPlan.creator.id);

          return (
            <Fade key={`action_plan_${aPlan.id}`} in unmountOnExit>
              <div className={classes.actionPlanWrapper}>
                <ActionPlanCard
                  className={cardClass}
                  translations={translations}
                  canViewCourses={canViewCourses}
                  actionPlan={aPlan}
                  statuses={statuses}
                  isCreatorAccessible={isCreatorAccessible}
                  isCreatedForCurrentUser={isCreatedForCurrentUser}
                  isDisabled={isDisabled}
                  canView={canView}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  canAcceptProposal={canAcceptProposal}
                  isCreatedByMe={isCreatedByMe}
                  canAccessUserProfileCheck={canAccessUserProfileCheck}
                  goToUserProfilePage={goToUserProfilePage}
                  onCardClick={onCardClick}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onManageTasks={onManageTasks}
                  onPreviewDescription={handleOpenDescriptionDialog}
                  onDismissProposal={onDismissProposal}
                />
              </div>
            </Fade>
          );
        })}
      </TransitionGroup>
      <PreviewDescriptionDialog
        isOpened={!!selectedActionPlan}
        title={translations.description}
        description={selectedActionPlan?.description}
        onClose={handleCloseDescriptionDialog}
      />
    </div>
  );
};

ActionPlanCards.propTypes = {
  className: PropTypes.string,
  cardClass: PropTypes.string,
  translations: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  user: PropTypes.object,
  actionPlans: PropTypes.arrayOf(PropTypes.shape({})),
  statuses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  canViewCourses: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onCardClick: PropTypes.func,
  onManageTasks: PropTypes.func,
  onDismissProposal: PropTypes.func,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  goToUserProfilePage: PropTypes.func.isRequired,
};

export default ActionPlanCards;
